import { mergeMap } from 'rxjs/operators';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ProfessionalLogService } from 'components/modal-windows/step-details/log-details/professional-log.service';
import { StepService } from 'services/professional/step/step.service';
import { MessageQueryService } from '@certemy/ui-components';
import { ROLE_CODES } from 'constants/role.constants';
import { SUPERVISOR_STATUSES } from 'constants/status.constants';
import { PAGES } from '../../../log-details/log-details.component';
import { CertemyValidators } from 'validators/validators';
import { email, required, withMessage, isExist } from '@certemy/common';
import { getMergedFormErrors } from 'utils/helpers';
import _get from 'lodash-es/get';
import moment from 'moment-mini';
import { DEFAULT_EXPIRATION_DATE_DAYS, DefaultInvitationMessagePlaceholder } from 'constants/log-step.constants';

export interface ResendInviteData {
  expirationDate: string;
}

const requiredWithMessage = withMessage('Please provide information in all required fields', required);

@Component({
  selector: 'cm-invitation',
  templateUrl: './invitation.component.html',
  styleUrls: ['./invitation.component.scss', '../../../step-details.shared.scss'],
})
export class InvitationComponent implements OnInit {
  @Input() step;
  @Input() stepEditableStatus: boolean;
  @Output() close: EventEmitter<any> = new EventEmitter();
  @Output() dismissModal: EventEmitter<any> = new EventEmitter();
  @Output() selectPage: EventEmitter<number> = new EventEmitter();

  invitationForm: UntypedFormGroup;
  showInvitationForm = false;
  STATUSES = SUPERVISOR_STATUSES;
  realNumericFieldValidation = CertemyValidators.realNumericFieldValidation;
  showErrors = false;
  validationMessage = '';
  readonly defaultInvitationMessagePlaceholder = DefaultInvitationMessagePlaceholder;

  get datePickerMinDate() {
    return moment().add(1, 'day').format('YYYY-MM-DD');
  }
  get datePickerMaxDate() {
    return moment().add(1, 'year').format('YYYY-MM-DD');
  }

  constructor(
    private fb: UntypedFormBuilder,
    private logService: ProfessionalLogService,
    private stepService: StepService,
    private messageService: MessageQueryService,
  ) {}

  ngOnInit() {
    this.checkIfInviteIsAvailable();
  }

  showForm(data = {}) {
    const draftFirstName = _get(data, 'first_name', '');
    const draftLastName = _get(data, 'last_name', '');
    const draftEmail = _get(data, 'email', '');
    const draftMessage = _get(data, 'invitation_message', '');
    this.invitationForm = this.fb.group({
      first_name: this.fb.control(draftFirstName, requiredWithMessage),
      last_name: this.fb.control(draftLastName, requiredWithMessage),
      email: this.fb.control(draftEmail, [
        requiredWithMessage,
        withMessage('Please provide valid email', email),
        withMessage('Please provide unique email', isExist(this.supervisorsEmails)),
      ]),
      expiration_days: DEFAULT_EXPIRATION_DATE_DAYS,
      invitation_message: this.fb.control(draftMessage),
    });
    this.showInvitationForm = true;
  }

  checkValidation() {
    this.showErrors = false;
    if (this.invitationForm.valid) {
      this.sendInvitation();
    } else {
      this.showErrors = true;
      this.validationMessage =
        getMergedFormErrors(this.invitationForm)
          .map((errors) => Object.values(errors).find((el) => typeof el === 'string'))
          .filter((hasValidationMessage) => !!hasValidationMessage)[0] ||
        'Please provide information in all required fields';
    }
  }

  sendInvitation() {
    const supervisor = {
      ...this.invitationForm.value,
      role_id: ROLE_CODES.SUPERVISOR,
      step_instance_id: this.step.step_instance_id,
    };

    this.logService
      .inviteSupervisor({
        users: [supervisor],
      })
      .pipe(mergeMap(() => this.stepService.getCertificateStep(this.step.step_instance_id)))
      .subscribe((response) => {
        this.step = response;
        this.invitationForm = null;
        this.messageService.addMessage('Supervisor was invited successfully');
        this.checkIfInviteIsAvailable();
      });
  }

  checkFormChanges() {
    if (this.invitationForm && this.invitationForm.dirty) {
      this.logService.draftSuperviserData = this.invitationForm.value;
      this.selectPage.emit(PAGES.INV_CONFIRM);
    } else {
      this.onDismissModal();
    }
  }

  onDismissModal() {
    this.logService.draftSuperviserData = null;
    this.dismissModal.emit();
  }

  showResendControls(supervisor) {
    supervisor.resendControlsVisible = true;
  }

  resendInvitation(supervisor) {
    supervisor.resendControlsVisible = false;

    this.logService
      .resendInviteSupervisor(supervisor.invitation_id, { expirationDate: supervisor.expiration_date })
      .subscribe(() => {
        this.messageService.addMessage('Invite for Supervisor was resend successfully');
      });
  }

  private get supervisorsEmails(): { [email: string]: boolean } {
    return this.step.content.supervisors.reduce((acc, supervisor) => ({ ...acc, [supervisor.email]: true }), {});
  }

  private checkIfInviteIsAvailable() {
    this.showInvitationForm = !!this.logService.draftSuperviserData;
    if (this.showInvitationForm) {
      this.showForm(this.logService.draftSuperviserData);
    }
  }
}
